import { type Page, type RescoreStatus, type RescoreSummary, type ScoreBand } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** Re-scores (task 44, decision 0026): staff propose, two others approve; auditors look. */

export interface RescoreItem {
  id?: string;
  assessment_id: string;
  customer_name: string;
  title: string;
  from_score: number | null;
  from_band: ScoreBand | null;
  from_cap: number | null;
  to_score: number;
  to_band: ScoreBand;
  to_cap: number | null;
  outcome?: 'planned' | 'applied' | 'skipped';
  skipped_reason?: string | null;
}

export interface Rescore {
  id: string;
  status: RescoreStatus;
  note: string;
  as_of: string;
  summary: RescoreSummary;
  requested_by: string;
  requested_by_name: string | null;
  requested_at: string;
  decided_by: string | null;
  decided_by_name: string | null;
  decision_note: string | null;
  decided_at: string | null;
}

export interface RescoreDetail extends Rescore {
  needed: number;
  approvals: { approver: string; approver_name: string | null; note: string | null; created_at: string }[];
  items: RescoreItem[];
}

export function useRescoreAccess() {
  const { tenant, me } = useSession();
  return {
    sees: tenant?.kind !== 'platform' && can(tenant, 'rescores.view'),
    acts: tenant?.kind !== 'platform' && can(tenant, 'rescores.create', 'rescores.approve'),
    permissions: tenant?.permissions ?? [],
    userId: me?.user.id ?? '',
  };
}

const k = (t: string | undefined, ...rest: unknown[]) => ['rescores', t, ...rest] as const;

/** The open proposal, if there is one; earlier re-scores page through the list view (task 60). */
export function usePendingRescore() {
  const { tenant } = useSession();
  return useQuery({ queryKey: k(tenant?.id, 'pending'), queryFn: () => api<Page<Rescore>>('GET', '/rescores?status=pending&page_size=12').then((p) => p.items[0] ?? null), enabled: !!tenant }); // prettier-ignore
}

export function useRescore(id: string | null) {
  const { tenant } = useSession();
  return useQuery({ queryKey: k(tenant?.id, id), queryFn: () => api<RescoreDetail>('GET', `/rescores/${id}`), enabled: !!tenant && !!id }); // prettier-ignore
}

/** What a re-score would do now. Read-only. */
export function useRescorePreview() {
  return useMutation({
    mutationFn: () => api<{ as_of: string; summary: RescoreSummary; items: RescoreItem[] }>('POST', '/rescores/dry-run'),
  }); // prettier-ignore
}

export function useRescoreAction() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (
      a:
        | { type: 'propose'; note: string }
        | { type: 'approve'; id: string; note?: string }
        | { type: 'reject'; id: string; note: string }
        | { type: 'withdraw'; id: string },
    ) =>
      a.type === 'propose'
        ? api<RescoreDetail>('POST', '/rescores', { note: a.note })
        : api<RescoreDetail>('POST', `/rescores/${a.id}/${a.type}`, a.type === 'withdraw' ? undefined : a.note ? { note: a.note } : {}), // prettier-ignore
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: k(tenant?.id) });
      void refreshLists(qc);
    },
  });
}
